<template>
  <div class="renderPage prov-container" :class="{ 'theme--dark': $vuetify.theme.dark }">

    <!-- TOOLBAR CABECERA -->
    <v-toolbar flat height="56" :color="$vuetify.theme.dark ? '#1e293b' : '#f1f5f9'" class="prov-toolbar px-2">
      <v-icon class="mr-2" :color="$vuetify.theme.dark ? 'teal lighten-2' : 'blue-grey darken-3'">mdi-handshake-outline</v-icon>
      <v-toolbar-title class="font-weight-black text-uppercase prov-toolbar-title" :class="$vuetify.theme.dark ? 'white--text' : 'blue-grey--text text--darken-4'">
        Gestión de Proveedores
      </v-toolbar-title>
      <v-spacer />
      <v-btn depressed color="primary" class="prov-btn font-weight-bold" @click="incluir()">
        <v-icon left small>mdi-plus</v-icon> Nuevo Proveedor
      </v-btn>
    </v-toolbar>

    <!-- TABLA -->
    <v-card flat class="prov-table-card mx-0" :color="$vuetify.theme.dark ? '#0f172a' : 'white'">
      <v-toolbar flat height="52" :color="$vuetify.theme.dark ? '#0f172a' : 'white'" class="px-3">
        <v-text-field
          v-model="search"
          append-icon="mdi-magnify"
          label="Buscar por nombre, cédula o código..."
          single-line hide-details outlined dense
          class="prov-search"
          style="max-width:420px"
        ></v-text-field>
        <v-spacer />
        <v-btn icon :color="$vuetify.theme.dark ? 'blue-grey lighten-2' : 'grey darken-1'" @click="getData()">
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </v-toolbar>

      <v-data-table
        :headers="headers"
        :items="items"
        :search="search"
        class="prov-table"
        :items-per-page="25"
        :footer-props="{ 'items-per-page-options': [25, 50, 100, -1] }"
        @click:row="editItem"
        style="cursor:pointer"
      >
        <template v-slot:[`item.codigo`]="{ item }">
          <span class="prov-code-badge">{{ item.codigo }}</span>
        </template>

        <template v-slot:[`item.nombre`]="{ item }">
          <div class="d-flex flex-column py-1">
            <span class="font-weight-bold text-body-2">{{ item.nombre }}</span>
            <span class="caption" :class="$vuetify.theme.dark ? 'blue-grey--text text--lighten-1' : 'grey--text'">{{ item.nombreComercial }}</span>
          </div>
        </template>

        <template v-slot:[`item.cedula`]="{ item }">
          <div class="d-flex align-center">
            <v-icon small class="mr-1" :class="$vuetify.theme.dark ? 'blue-grey--text' : 'grey--text'">mdi-card-account-details-outline</v-icon>
            <span class="body-2 font-weight-medium">{{ item.cedula }}</span>
          </div>
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <v-btn color="blue-grey darken-2" depressed dark style="min-width:36px;height:36px;padding:0;border-radius:4px" @click.stop="editItem(item)">
            <v-icon size="18">mdi-pencil-outline</v-icon>
          </v-btn>
        </template>

        <template v-slot:no-data>
          <div class="pa-8 text-center">
            <v-icon size="48" :color="$vuetify.theme.dark ? 'blue-grey darken-1' : 'grey lighten-1'" class="mb-3">mdi-account-off-outline</v-icon>
            <p :class="$vuetify.theme.dark ? 'blue-grey--text' : 'grey--text'">No hay proveedores registrados aún.</p>
            <v-btn color="primary" depressed small @click="getData()">Cargar de nuevo</v-btn>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <!-- DRAWER FORMULARIO -->
    <v-navigation-drawer v-model="drawerForm" fixed right temporary width="650" class="prov-drawer">
      <div class="d-flex flex-column fill-height">

        <!-- HEADER DRAWER -->
        <div class="prov-drawer-header d-flex align-center px-4 py-3" :class="$vuetify.theme.dark ? 'prov-drawer-header--dark' : ''">
          <v-icon color="white" class="mr-3">{{ editedIndex > -1 ? 'mdi-account-edit-outline' : 'mdi-account-plus-outline' }}</v-icon>
          <div>
            <div class="white--text font-weight-black text-uppercase" style="letter-spacing:1px;font-size:0.95rem;">
              {{ editedIndex > -1 ? 'Editar Proveedor' : 'Nuevo Proveedor' }}
            </div>
            <div class="caption white--text" style="opacity:0.7">{{ editedItem.codigo || 'SIN CÓDIGO' }} · Información fiscal y de contacto</div>
          </div>
          <v-spacer />
          <v-btn icon dark small @click="cerrar()"><v-icon>mdi-close</v-icon></v-btn>
        </div>

        <!-- FORMULARIO -->
        <div class="flex-grow-1 overflow-y-auto pa-5">
          <v-form ref="form" v-model="valid">

            <!-- IDENTIFICACIÓN FISCAL -->
            <div class="prov-section-label mb-3">
              <v-icon small class="mr-1" :color="$vuetify.theme.dark ? 'teal lighten-2' : 'blue-grey darken-3'">mdi-card-account-details-outline</v-icon>
              <span class="font-weight-black text-caption text-uppercase" style="letter-spacing:1px" :class="$vuetify.theme.dark ? 'white--text' : 'blue-grey--text text--darken-3'">Identificación Fiscal</span>
            </div>
            <v-card flat class="prov-section-card pa-4 mb-4">
              <v-row dense>
                <v-col cols="12" sm="4">
                  <v-select
                    v-model="editedItem.tipoId"
                    :items="tiposIdentificacion"
                    item-text="descripcion"
                    item-value="tipoId"
                    label="Tipo de Cédula"
                    outlined dense
                    prepend-inner-icon="mdi-account-badge-outline"
                  ></v-select>
                </v-col>
                <v-col cols="12" sm="5">
                  <v-text-field
                    v-model="editedItem.cedula"
                    label="Número de Cédula"
                    outlined dense type="number"
                    prepend-inner-icon="mdi-card-account-details-outline"
                    class="prov-no-spinner"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="3">
                  <v-btn color="primary" block depressed height="40" class="prov-btn font-weight-bold" :loading="loadingHacienda" @click="getClienteHacienda">
                    <v-icon left size="16">mdi-magnify-scan</v-icon> Hacienda
                  </v-btn>
                </v-col>
                <v-col cols="12" sm="8">
                  <v-text-field
                    v-model="editedItem.nombre"
                    label="Nombre o Razón Social"
                    outlined dense
                    prepend-inner-icon="mdi-account"
                    :rules="nameRules"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="4">
                  <v-select
                    v-model="editedItem.moneda"
                    :items="monedas"
                    item-text="md_nombre"
                    item-value="md_codigo"
                    label="Moneda"
                    outlined dense
                    prepend-inner-icon="mdi-currency-usd"
                  ></v-select>
                </v-col>
                <v-col cols="12">
                  <v-text-field
                    v-model="editedItem.nombreComercial"
                    label="Nombre Comercial"
                    outlined dense
                    prepend-inner-icon="mdi-store-outline"
                  ></v-text-field>
                </v-col>
              </v-row>

              <v-divider class="my-3" />
              <div class="d-flex align-center justify-space-between px-1">
                <div>
                  <div class="font-weight-bold text-body-2">Factura Electrónica</div>
                  <div class="caption" :class="$vuetify.theme.dark ? 'blue-grey--text' : 'grey--text'">El proveedor emite comprobantes electrónicos</div>
                </div>
                <v-switch v-model="editedItem.esReceptor" inset dense color="primary" hide-details class="mt-0 pt-0" @change="changeEsReceptor"></v-switch>
              </div>
            </v-card>

            <!-- CONTACTO -->
            <div class="prov-section-label mb-3">
              <v-icon small class="mr-1" :color="$vuetify.theme.dark ? 'teal lighten-2' : 'blue-grey darken-3'">mdi-email-outline</v-icon>
              <span class="font-weight-black text-caption text-uppercase" style="letter-spacing:1px" :class="$vuetify.theme.dark ? 'white--text' : 'blue-grey--text text--darken-3'">Contacto y Notificación</span>
            </div>
            <v-card flat class="prov-section-card pa-4 mb-4">
              <v-text-field
                v-model="editedItem.correo"
                label="Correo para Facturación"
                outlined dense
                prepend-inner-icon="mdi-email-outline"
              ></v-text-field>
            </v-card>

            <!-- UBICACIÓN -->
            <v-expansion-panels flat class="prov-panels" :value="0">
              <v-expansion-panel class="prov-expansion-panel">
                <v-expansion-panel-header class="font-weight-bold text-caption text-uppercase" style="letter-spacing:1px;min-height:48px">
                  <div class="d-flex align-center">
                    <v-icon small class="mr-2" :color="$vuetify.theme.dark ? 'teal lighten-2' : 'blue-grey darken-3'">mdi-map-marker-outline</v-icon>
                    <span :class="$vuetify.theme.dark ? 'white--text' : 'blue-grey--text text--darken-3'">Ubicación y Contacto</span>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-row dense class="pt-2">
                    <v-col cols="12" sm="4">
                      <v-select v-model="editedItem.provincia" :items="provincias" item-text="pr_nombre" item-value="pr_codigo" label="Provincia" outlined dense @change="changeProvincia"></v-select>
                    </v-col>
                    <v-col cols="12" sm="4">
                      <v-select v-model="editedItem.canton" :items="cantones" item-text="ct_nombre" item-value="ct_codigo" label="Cantón" outlined dense @change="changeCantones"></v-select>
                    </v-col>
                    <v-col cols="12" sm="4">
                      <v-select v-model="editedItem.distrito" :items="distritos" item-text="dt_nombre" item-value="dt_codigo" label="Distrito" outlined dense @change="changeDistritos"></v-select>
                    </v-col>
                    <v-col cols="12" sm="4">
                      <v-text-field v-model="editedItem.areaTel" label="Área" outlined dense type="number" prepend-inner-icon="mdi-phone-outline"></v-text-field>
                    </v-col>
                    <v-col cols="12" sm="8">
                      <v-text-field v-model="editedItem.telefono" label="Teléfono" outlined dense type="number"></v-text-field>
                    </v-col>
                    <v-col cols="12">
                      <v-textarea v-model="editedItem.direccion" label="Dirección Exacta" outlined dense rows="2" hide-details></v-textarea>
                    </v-col>
                  </v-row>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>

          </v-form>
        </div>

        <!-- FOOTER ACCIONES -->
        <div class="prov-drawer-footer d-flex align-center px-4 py-3" :class="$vuetify.theme.dark ? 'prov-drawer-footer--dark' : ''">
          <v-btn v-if="editedIndex > -1" color="error" text class="prov-btn font-weight-bold" @click="deleteItem()">
            <v-icon left small>mdi-trash-can-outline</v-icon> Eliminar
          </v-btn>
          <v-spacer />
          <v-btn text class="mr-3 font-weight-bold prov-btn" @click="cerrar()">Cancelar</v-btn>
          <v-btn color="primary" depressed class="px-8 font-weight-bold prov-btn" :loading="loading" @click="save()">
            {{ editedIndex > -1 ? 'GUARDAR' : 'CREAR PROVEEDOR' }}
          </v-btn>
        </div>
      </div>
    </v-navigation-drawer>

    <!-- DIALOG CONFIRMAR ELIMINAR -->
    <v-dialog v-model="dialogEliminar" max-width="400" persistent>
      <v-card flat class="prov-dialog-card">
        <div class="prov-drawer-header d-flex align-center px-4 py-3" :class="$vuetify.theme.dark ? 'prov-drawer-header--dark' : ''">
          <v-icon color="white" small class="mr-2">mdi-alert-outline</v-icon>
          <span class="white--text font-weight-black text-uppercase" style="font-size:0.85rem;letter-spacing:1px">¿Eliminar proveedor?</span>
          <v-spacer />
          <v-btn icon dark x-small @click="dialogEliminar = false"><v-icon small>mdi-close</v-icon></v-btn>
        </div>
        <div class="pa-5">
          <p class="body-2 mb-4" :class="$vuetify.theme.dark ? 'blue-grey--text text--lighten-2' : 'grey--text text--darken-1'">Esta acción no se puede deshacer.</p>
          <div class="d-flex justify-end">
            <v-btn text class="mr-2 font-weight-bold prov-btn" @click="dialogEliminar = false">Cancelar</v-btn>
            <v-btn color="error" depressed class="prov-btn font-weight-bold px-6" @click="confirmarEliminar">Eliminar</v-btn>
          </div>
        </div>
      </v-card>
    </v-dialog>

    <Snackbar v-bind:text="snackText" v-bind:snackbar.sync="snackValue" />
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import axios from "axios";
import Snackbar from "./shared/Snackbar.vue";

export default {
  components: {
    Snackbar,
  },
  data() {
    return {
      drawerForm: false,
      dialogEliminar: false,
      loading: false,
      loadingHacienda: false,
      valid: false,
      search: "",
      snackText: "",
      snackValue: false,
      esRec: false,
      headers: [
        { text: "Código", value: "codigo", sortable: true },
        { text: "Proveedor / Comercial", value: "nombre", sortable: true },
        { text: "Cédula", value: "cedula", sortable: true },
        { text: "Acciones", value: "actions", sortable: false, align: 'right' },
      ],
      items: [],
      tiposIdentificacion: [],
      monedas: [],
      provincias: [],
      cantones: [],
      distritos: [],
      editedIndex: -1,
      editedItem: {
        codigo: "",
        nombre: "",
        moneda: "CRC",
        provincia: "",
        canton: "",
        distrito: "",
        barrio: "",
        direccion: "",
        esReceptor: false,
        correo: "",
        tipoId: "",
        nombreComercial: "",
        areaTel: "506",
        telefono: "",
        cedula: "",
        cia: "",
      },
      defaultItem: {
        codigo: "",
        nombre: "",
        moneda: "CRC",
        provincia: "",
        canton: "",
        distrito: "",
        barrio: "",
        direccion: "",
        esReceptor: false,
        correo: "",
        tipoId: "",
        nombreComercial: "",
        areaTel: "506",
        telefono: "",
        cedula: "",
        cia: "",
      },
      nameRules: [(v) => !!v || "Este campo es requerido"],
    };
  },

  computed: {
    ...mapGetters(["user", "strCia"]),
  },

  created() {
    this.getData();
    this.getTiposIdentificacion();
    this.getMonedas();
    this.getProvincias();
  },

  methods: {
    getData() {
      axios.get("api/Proveedores/getProveedoresByCompany?cia=" + this.strCia)
        .then(res => { this.items = res.data; })
        .catch(err => console.error(err));
    },

    getTiposIdentificacion() {
      axios.get("api/General/getTiposIdentificacion").then(res => { this.tiposIdentificacion = res.data; });
    },

    getMonedas() {
      axios.get("api/General/getMonedas").then(res => { this.monedas = res.data; });
    },

    getProvincias() {
      axios.get("api/General/getProvincias").then(res => { this.provincias = res.data; });
    },

    getCantones() {
      axios.get("api/General/getCantones?provincia=" + this.editedItem.provincia).then(res => { this.cantones = res.data; });
    },

    getDistritos() {
      axios.get("api/General/getDistritos?provincia=" + this.editedItem.provincia + "&canton=" + this.editedItem.canton).then(res => { this.distritos = res.data; });
    },

    changeProvincia() { this.editedItem.canton = ""; this.editedItem.distrito = ""; this.editedItem.direccion = ""; this.getCantones(); },
    changeCantones() { this.editedItem.distrito = ""; this.editedItem.direccion = ""; this.getDistritos(); },
    changeDistritos() { this.actualizarDireccionUbicacion(); },

    actualizarDireccionUbicacion() {
      const prov = this.provincias.find(p => p.pr_codigo === this.editedItem.provincia);
      const cant = this.cantones.find(c => c.ct_codigo === this.editedItem.canton);
      const dist = this.distritos.find(d => d.dt_codigo === this.editedItem.distrito);
      const partes = [prov && prov.pr_nombre, cant && cant.ct_nombre, dist && dist.dt_nombre].filter(Boolean);
      if (partes.length) this.editedItem.direccion = partes.join(", ");
    },

    changeEsReceptor() {
      this.esRec = this.editedItem.esReceptor;
    },

    async getClienteHacienda() {
      if (!this.editedItem.cedula) return;
      this.loadingHacienda = true;
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 5000);
      let filled = false;
      try {
        const res = await axios.get("https://api.hacienda.go.cr/fe/ae?identificacion=" + this.editedItem.cedula, { signal: controller.signal });
        clearTimeout(timeout);
        if (!filled) { filled = true; this.aplicarDatosContribuyente(res.data); }
      } catch {
        clearTimeout(timeout);
        if (!filled) {
          try {
            const res = await axios.get("api/General/getCedulaGometa?cedula=" + this.editedItem.cedula);
            filled = true;
            this.aplicarDatosContribuyente(res.data);
          } catch {
            this.snackText = "No se pudo obtener información de esta cédula";
            this.snackValue = true;
          }
        }
      } finally {
        this.loadingHacienda = false;
      }
    },

    aplicarDatosContribuyente(data) {
      if (data.nombre) {
        this.editedItem.nombre = data.nombre;
        this.editedItem.nombreComercial = data.nombre;
      }
      if (data.tipoIdentificacion) this.editedItem.tipoId = data.tipoIdentificacion;
      if (data.situacion && data.situacion.estado === "Inscrito") {
        this.editedItem.esReceptor = true;
        this.esRec = true;
      }
    },

    editItem(item) {
      this.editedIndex = this.items.indexOf(item);
      this.editedItem = Object.assign({}, item);
      this.changeEsReceptor();
      if (this.editedItem.provincia) {
        axios.get("api/General/getCantones?provincia=" + this.editedItem.provincia).then(res => {
          this.cantones = res.data;
          if (this.editedItem.canton) {
            axios.get("api/General/getDistritos?provincia=" + this.editedItem.provincia + "&canton=" + this.editedItem.canton).then(res2 => {
              this.distritos = res2.data;
            });
          }
        });
      }
      this.drawerForm = true;
    },

    incluir() {
      this.editedIndex = -1;
      this.editedItem = Object.assign({}, this.defaultItem);
      this.esRec = false;
      axios.get("api/Proveedores/getCodigoProveedores?cia=" + this.strCia).then(res => {
        this.editedItem.codigo = res.data;
      });
      this.drawerForm = true;
    },

    save() {
      if (!this.$refs.form.validate()) return;
      this.loading = true;
      let me = this;
      this.editedItem.cia = this.strCia;

      const url = this.editedIndex > -1 ? "api/Proveedores/updateProveedores" : "api/Proveedores/insertProveedores";
      axios.post(url, this.editedItem)
        .then(() => {
          me.snackText = me.editedIndex > -1 ? "Proveedor actualizado" : "Proveedor registrado con éxito";
          me.snackValue = true;
          me.getData();
          me.cerrar();
        })
        .catch(err => {
          me.snackText = "Error al guardar: " + (err.response && err.response.data ? err.response.data : err.message);
          me.snackValue = true;
        })
        .finally(() => { me.loading = false; });
    },

    deleteItem() {
      this.dialogEliminar = true;
    },

    confirmarEliminar() {
      let me = this;
      this.dialogEliminar = false;
      axios.get("api/Proveedores/deleteProveedores?cia=" + this.strCia + "&codigo=" + this.editedItem.codigo)
        .then(() => {
          me.snackText = "Proveedor eliminado";
          me.snackValue = true;
          me.getData();
          me.cerrar();
        })
        .catch(err => console.error(err));
    },

    cerrar() {
      this.drawerForm = false;
      this.$nextTick(() => {
        this.editedIndex = -1;
        this.editedItem = Object.assign({}, this.defaultItem);
        this.esRec = false;
      });
    },
  },
};
</script>

<style scoped>
/* ===== CONTAINER ===== */
.prov-container {
  background-color: #f1f5f9;
  min-height: 100vh;
}
.theme--dark.prov-container {
  background-color: #0f172a;
}

/* ===== TOOLBAR ===== */
.prov-toolbar {
  border-bottom: 1px solid rgba(0, 0, 0, 0.1) !important;
}
.theme--dark .prov-toolbar {
  border-bottom: 1px solid rgba(255, 255, 255, 0.07) !important;
}
.prov-toolbar-title {
  font-size: 0.95rem !important;
  letter-spacing: 1.5px;
}

/* ===== BUTTONS ===== */
.prov-btn {
  border-radius: 4px !important;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  font-size: 0.75rem !important;
}

/* ===== TABLE ===== */
.prov-table-card {
  border-bottom: 1px solid rgba(0, 0, 0, 0.08) !important;
}
.prov-table { background: transparent !important; }
.prov-table >>> thead th {
  background-color: #37474f !important;
  color: #ffffff !important;
  text-transform: uppercase;
  font-size: 0.72rem !important;
  font-weight: 700 !important;
  letter-spacing: 0.07em;
  border-bottom: none !important;
}
.prov-table >>> tbody tr { cursor: pointer; }
.prov-table >>> tbody tr:hover {
  background-color: rgba(55, 71, 79, 0.05) !important;
}
.theme--dark .prov-table >>> tbody tr:hover {
  background-color: rgba(255, 255, 255, 0.05) !important;
}

/* ===== CODE BADGE ===== */
.prov-code-badge {
  font-family: monospace;
  font-size: 0.8rem;
  font-weight: 700;
  padding: 2px 6px;
  background: rgba(55, 71, 79, 0.08);
  border-radius: 3px;
  color: #37474f;
}
.theme--dark .prov-code-badge {
  background: rgba(255, 255, 255, 0.1);
  color: #b0bec5;
}

/* ===== SEARCH ===== */
.prov-search >>> .v-input__slot {
  border-radius: 4px !important;
}

/* ===== DRAWER ===== */
.prov-drawer >>> .v-navigation-drawer__content {
  background-color: #ffffff !important;
}
.theme--dark .prov-drawer >>> .v-navigation-drawer__content {
  background-color: #0f172a !important;
}

/* ===== DRAWER HEADER ===== */
.prov-drawer-header {
  background-color: #37474f;
  flex-shrink: 0;
}
.prov-drawer-header--dark {
  background-color: #263238 !important;
}

/* ===== DRAWER FOOTER ===== */
.prov-drawer-footer {
  background-color: #f8fafc;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  flex-shrink: 0;
}
.prov-drawer-footer--dark {
  background-color: #1e293b !important;
  border-top: 1px solid rgba(255, 255, 255, 0.07) !important;
}

/* ===== SECTION LABEL ===== */
.prov-section-label {
  display: flex;
  align-items: center;
}

/* ===== SECTION CARD ===== */
.prov-section-card {
  border: 1px solid rgba(0, 0, 0, 0.07) !important;
  border-radius: 4px !important;
  background-color: #ffffff !important;
}
.theme--dark .prov-section-card {
  border-color: rgba(255, 255, 255, 0.07) !important;
  background-color: #1e293b !important;
}

/* ===== DIALOG CARD ===== */
.prov-dialog-card {
  border-radius: 4px !important;
  overflow: hidden;
}

/* ===== EXPANSION PANEL (UBICACIÓN) ===== */
.prov-panels {
  border: 1px solid rgba(0, 0, 0, 0.07) !important;
  border-radius: 4px !important;
  overflow: hidden;
}
.theme--dark .prov-panels {
  border-color: rgba(255, 255, 255, 0.07) !important;
}
.prov-panels >>> .v-expansion-panel {
  background-color: #ffffff !important;
}
.theme--dark .prov-panels >>> .v-expansion-panel {
  background-color: #1e293b !important;
}

/* ===== NO SPINNER EN INPUTS NUMÉRICOS ===== */
.prov-no-spinner >>> input::-webkit-outer-spin-button,
.prov-no-spinner >>> input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
.prov-no-spinner >>> input[type=number] {
  -moz-appearance: textfield;
}
</style>
